/**
 * ReferralDialogChrome — the small shared pieces of the referral dialogs
 * (pause, complete): the overlay + panel, the header with the miniature
 * business card, the plum field label and the DD/MM-style hint. Kept here so
 * the two dialogs and the capture modal share one look (the approved
 * "Dialogs" artboard) without duplicating styles.
 *
 * Plain elements, no animation library, so the dialogs render identically
 * in the browser and in the static harness used for screenshots.
 */

import React from 'react';

/** Full-screen overlay + white panel. Click on the backdrop = onClose. */
export function DialogFrame({ open, onClose, busy = false, width = 480, label, children }) {
  if (!open) return null;
  return (
    <div style={styles.overlay} onClick={() => !busy && onClose && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        style={{ ...styles.panel, maxWidth: `${width}px` }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/** Miniature business card + title + subtitle (the dialogs' header). */
export function DialogHead({ referral, title, subtitle }) {
  return (
    <div style={styles.head}>
      <div style={styles.mini} aria-hidden="true">
        <svg style={styles.miniClip} viewBox="0 0 16 36" fill="none" stroke="var(--cp-referral-clip)" strokeWidth="2">
          <path d="M4 30V7a3 3 0 0 1 6 0v22a5 5 0 0 1-10 0V9" />
        </svg>
        <div>
          <div style={styles.miniName}>{referral?.prospectName || ''}</div>
          <div style={styles.miniRef}>
            referred by <b style={{ color: 'var(--cp-referral-ink)' }}>{referral?.referrer?.displayName || ''}</b>
          </div>
        </div>
      </div>
      <div>
        <div style={styles.title}>{title}</div>
        {subtitle && <div style={styles.subtitle}>{subtitle}</div>}
      </div>
    </div>
  );
}

/** Plum, letter-spaced field label. */
export function FieldLabel({ htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} style={styles.label}>
      {children}
    </label>
  );
}

/** Muted one-line hint under a field. */
export function Hint({ children }) {
  return <div style={styles.hint}>{children}</div>;
}

/** Radio-style option tile (outcome, feedback). */
export function OptionTile({ name, value, checked, onChange, children, small, disabled }) {
  return (
    <label style={{ ...styles.opt, ...(checked ? styles.optOn : {}), ...(disabled ? { opacity: 0.6 } : {}) }}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={() => onChange && onChange(value)}
        disabled={disabled}
        style={styles.optInput}
      />
      <span>{children}</span>
      {small && <small style={styles.optSmall}>{small}</small>}
    </label>
  );
}

export const dialogStyles = {
  field: { display: 'flex', flexDirection: 'column' },
  row: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: 'auto', paddingTop: '6px' },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    height: '40px',
    border: '1.5px solid var(--cp-border)',
    borderRadius: '6px',
    padding: '0 12px',
    font: "500 14px 'Montserrat', 'DM Sans', sans-serif",
    color: 'var(--cp-text)',
    background: 'var(--cp-surface)',
  },
  textarea: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1.5px solid var(--cp-border)',
    borderRadius: '6px',
    padding: '8px 12px',
    font: "500 14px 'Montserrat', 'DM Sans', sans-serif",
    color: 'var(--cp-text)',
    background: 'var(--cp-surface)',
    resize: 'none',
    minHeight: '72px',
  },
  counter: {
    display: 'flex',
    justifyContent: 'flex-end',
    fontSize: '0.75rem',
    fontWeight: 500,
    marginTop: '4px',
  },
  primary: {
    height: '44px',
    padding: '0 20px',
    borderRadius: '6px',
    border: 0,
    font: "700 12px 'Montserrat', 'DM Sans', sans-serif",
    letterSpacing: '0.06em',
    cursor: 'pointer',
    background: 'var(--cp-referral-orange)',
    color: 'var(--cp-surface)',
  },
  secondary: {
    height: '44px',
    padding: '0 20px',
    borderRadius: '6px',
    font: "700 12px 'Montserrat', 'DM Sans', sans-serif",
    letterSpacing: '0.06em',
    cursor: 'pointer',
    background: 'transparent',
    color: 'var(--cp-primary)',
    border: '1.5px solid var(--cp-border)',
  },
  error: {
    background: 'rgba(231, 111, 81, 0.12)',
    border: '1px solid var(--cp-warning)',
    color: 'var(--cp-danger)',
    borderRadius: '8px',
    padding: '0.5rem 0.75rem',
    fontSize: '0.85rem',
  },
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(21, 55, 65, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100,
    padding: '1rem',
  },
  panel: {
    width: '100%',
    boxSizing: 'border-box',
    background: 'var(--cp-surface)',
    borderRadius: '12px',
    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.35)',
    padding: '28px 30px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
    maxHeight: '92vh',
    overflowY: 'auto',
    fontFamily: "'Montserrat', 'DM Sans', sans-serif",
    color: 'var(--cp-text)',
  },
  head: { display: 'flex', alignItems: 'center', gap: '12px' },
  mini: {
    width: '112px',
    height: '62px',
    flex: '0 0 112px',
    background: 'var(--cp-referral-card)',
    borderRadius: '3px',
    boxShadow: '0 2px 5px rgba(40, 20, 50, 0.18), 0 0 0 1px var(--cp-referral-card-edge) inset',
    transform: 'rotate(-1.5deg)',
    boxSizing: 'border-box',
    padding: '9px 10px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative',
  },
  miniClip: { position: 'absolute', left: '50%', top: '-4px', marginLeft: '-5px', width: '10px', height: '22px' },
  miniName: { fontSize: '10px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  miniRef: { fontSize: '6.5px', color: 'var(--cp-text-muted)', marginTop: '2px' },
  title: { fontSize: '18px', fontWeight: 700, color: 'var(--cp-primary)' },
  subtitle: { fontSize: '11.5px', color: 'var(--cp-text-muted)', marginTop: '3px' },
  label: {
    display: 'block',
    fontSize: '9.5px',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: 'var(--cp-referral-ink)',
    marginBottom: '6px',
  },
  hint: { fontSize: '10.5px', color: 'var(--cp-text-muted)', marginTop: '6px' },
  opt: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    height: '44px',
    padding: '0 14px',
    border: '1.5px solid var(--cp-border)',
    borderRadius: '6px',
    background: 'var(--cp-surface)',
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--cp-text)',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  optOn: { borderColor: 'var(--cp-referral-ink)', background: 'var(--cp-referral-sheet)' },
  optInput: { width: '16px', height: '16px', margin: 0, accentColor: 'var(--cp-referral-ink)', flex: '0 0 16px' },
  optSmall: { fontWeight: 500, color: 'var(--cp-text-muted)', fontSize: '11px', marginLeft: 'auto' },
};
